import { useState, useEffect, useMemo } from 'react';
import { Layers, AlertCircle, Trash2 } from 'lucide-react';
import type { Job, CrmAssignment } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { subscribeToAllJobs } from '../lib/jobs.js';
import { startMount, dropMountDatabase, getCrmAssignments } from '../lib/callables.js';
import { SourceBadge } from '../components/SourceBadge.js';
import { PIPELINE_COLS, PipelineCell } from '../components/MountFlow.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MountPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Array<Job & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  // Job IDs with an in-flight Mount/Drop click (optimistic UI until the doc updates).
  const [mounting, setMounting] = useState<Set<string>>(new Set());
  const [dropping, setDropping] = useState<Set<string>>(new Set());

  // Per-CRM mount assignments — a job is only mountable if its CRM has a method
  // assigned on the CRMs page.
  const [assignments, setAssignments] = useState<Record<string, CrmAssignment>>({});

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToAllJobs(
      (j) => { setJobs(j); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    getCrmAssignments()
      .then((r) => setAssignments(r.data.assignments))
      .catch(() => setAssignments({}));
  }, [user]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const completed = useMemo(
    () => jobs.filter((j) => j.status === 'complete'),
    [jobs],
  );

  function clearError(jobId: string) {
    setErrors((e) => {
      const { [jobId]: _omit, ...rest } = e;
      return rest;
    });
  }

  async function handleMount(jobId: string) {
    setMounting((prev) => new Set([...prev, jobId]));
    clearError(jobId);
    try {
      await startMount({ jobId });
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [jobId]: err instanceof Error ? err.message : 'Failed to start mount.',
      }));
    } finally {
      setMounting((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  }

  async function handleDrop(job: Job & { id: string }) {
    // A job may have mounted more than one database (e.g. Vincere's main DB plus
    // its calcdb companion) — name them all in the confirmation.
    const names = [job.rds?.dbName, ...(job.extraRds ?? []).map((r) => r.dbName)]
      .filter(Boolean)
      .join('", "');
    const target = names || job.clientName;
    if (
      !window.confirm(
        `Drop the mounted database "${target}" from the shared RDS instance? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDropping((prev) => new Set([...prev, job.id]));
    clearError(job.id);
    try {
      await dropMountDatabase({ jobId: job.id });
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [job.id]: err instanceof Error ? err.message : 'Failed to start drop.',
      }));
    } finally {
      setDropping((prev) => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#37352f] tracking-tight">Mount</h1>
        <p className="mt-0.5 text-sm text-[#73726e]">
          Bring completed imports online through the pipeline: RDS → DMS → Redshift → cleanup.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      ) : completed.length === 0 ? (
        <div className="border border-[#e9e9e7] rounded-lg px-4 py-12 text-center">
          <Layers size={24} className="mx-auto mb-2 text-[#d3d3d1]" />
          <p className="text-sm text-[#aeaaa4]">
            No completed imports yet. They'll appear here once the transfer finishes.
          </p>
        </div>
      ) : (
        <div className="border border-[#e9e9e7] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e9e9e7] bg-[#fafafa]">
                  <th className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap">
                    Name
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap">
                    System
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap">
                    Completed
                  </th>
                  {PIPELINE_COLS.map((col) => (
                    <th
                      key={col.key}
                      className="py-2 px-3 text-center text-xs font-medium text-[#aeaaa4] whitespace-nowrap"
                    >
                      {col.header}
                    </th>
                  ))}
                  <th className="py-2 px-3" />
                </tr>
              </thead>
              <tbody>
                {completed.map((job) => {
                  const phase = job.mountPhase ?? null;
                  const isMounting = mounting.has(job.id) || phase === 'mounting';
                  const isDropping = dropping.has(job.id) || phase === 'dropping';
                  const busy = isMounting || isDropping;
                  // A CRM is mountable only once it has a method assigned on the
                  // CRMs page; otherwise we surface "No method available".
                  const hasMethod = Boolean(assignments[job.sourceSystem]?.methodId);
                  // Mountable when never mounted, previously failed, or dropped.
                  const canMount =
                    !busy &&
                    hasMethod &&
                    (phase === null || phase === 'failed' || phase === 'dropped');
                  // Droppable once a database exists (mounted, or failed cleanup).
                  const canDrop =
                    !busy && !!job.rds && (phase === 'mounted' || phase === 'failed');
                  const rowError = errors[job.id] ?? job.mountError ?? null;
                  return (
                    <tr
                      key={job.id}
                      className="border-b border-[#f1f1ef] last:border-0 hover:bg-[#fafafa] transition-colors"
                    >
                      <td className="py-3 px-3 font-medium text-[#37352f] whitespace-nowrap align-top">
                        {job.clientName}
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap align-top">
                        <SourceBadge system={job.sourceSystem} />
                      </td>
                      <td className="py-3 px-3 text-xs text-[#73726e] whitespace-nowrap align-top">
                        {formatDate(job.completedAt)}
                      </td>
                      {PIPELINE_COLS.map((col) => (
                        <td key={col.key} className="py-3 px-3 text-center whitespace-nowrap align-top">
                          <PipelineCell col={col} stage={job.mountStage ?? null} />
                        </td>
                      ))}
                      <td className="py-3 px-3 text-right whitespace-nowrap align-top">
                        <div className="flex items-center justify-end gap-2">
                          {isMounting ? (
                            <span className="text-[11px] text-[#73726e]">
                              Mounting…
                              {typeof job.mountPercent === 'number'
                                ? ` ${Math.round(job.mountPercent)}%`
                                : ''}
                            </span>
                          ) : isDropping ? (
                            <span className="text-[11px] text-[#73726e]">Dropping…</span>
                          ) : !hasMethod ? (
                            <span
                              className="text-[11px] text-[#aeaaa4]"
                              title="No mount model has been built for this system yet. Ping Jordan in Slack to get one set up."
                            >
                              No mount model — ping Jordan in Slack
                            </span>
                          ) : null}
                          {canMount && (
                            <button
                              onClick={() => handleMount(job.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#2383e2] rounded hover:bg-[#1a6dc5] disabled:opacity-50 transition-colors"
                            >
                              <Layers size={12} />
                              {phase === 'failed' || phase === 'dropped' ? 'Retry mount' : 'Mount'}
                            </button>
                          )}
                          {canDrop && (
                            <button
                              onClick={() => handleDrop(job)}
                              title="Drop the mounted database (rollback)"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#e03e3e] border border-[#f3c6c6] rounded hover:bg-[#fdeaea] disabled:opacity-50 transition-colors"
                            >
                              <Trash2 size={12} />
                              Drop
                            </button>
                          )}
                        </div>
                        {rowError && (
                          <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-[#e03e3e] max-w-[260px] ml-auto">
                            <AlertCircle size={11} className="shrink-0" />
                            <span className="truncate" title={rowError}>{rowError}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
